<mc-toast #saveToastRef appearance="success" position="bottom-right" duration="3000">
  <mc-notification appearance="success" heading="Saved" body="VSC settings saved successfully." closable></mc-notification>
</mc-toast>

<mc-modal fit="medium" dimension="small" width="600px" heading="Delete setting" [open]="showDeleteModal" (closed)="showDeleteModal = false">
  <p>This setting will be deleted and all vendors associated with this setting will be moved to default setting. Are you sure?</p>
  <mc-button slot="primaryAction" appearance="primary" [loading]="isDeleting" (click)="confirmDelete()" (keydown.enter)="confirmDelete()"
    >Delete</mc-button
  >
  <mc-button
    slot="secondaryAction"
    appearance="neutral"
    variant="outlined"
    (click)="showDeleteModal = false"
    (keydown.enter)="showDeleteModal = false"
    >Cancel</mc-button
  >
</mc-modal>

<!-- The small dimension preset is a fixed 225px tall, which scrolls the period selector out of view, so the height fits the content instead. -->
<mc-modal
  fit="medium"
  dimension="small"
  width="600px"
  height="auto"
  heading="Recalculate Scores"
  [open]="showCalculateModal"
  (closed)="showCalculateModal = false">
  @if (calculateAllVendors) {
    <p class="mds-body">
      Scores are calculated automatically on the <strong>12th of every month</strong>, for the previous month — e.g. at 00:00 UTC on 12 Sept
      2026, scores will be calculated for all vendors for Aug 2026. Any changes to global settings are automatically picked up in the next
      scheduled run. If you want to recalculate scores for <strong> all vendors</strong> that have already been calculated, choose one or
      more months from the dropdown below and click Calculate.
    </p>
  } @else {
    <p class="mds-body">
      Scores are calculated automatically on the <strong>12th of every month</strong>, for the previous month — e.g. at 00:00 UTC on 12 Sept
      2026, scores will be calculated for <strong>{{ calculateVendorIds.length }} vendor(s)</strong> for Aug 2026. Any changes to vendor
      settings are automatically picked up in the next scheduled run. If you want to recalculate scores for
      <strong>{{ calculateVendorIds.length }} vendor(s)</strong> that have already been calculated, choose one or more months from the
      dropdown below and click Calculate.
    </p>
  }
  <div class="calculate-period">
    <mc-multi-select
      fit="small"
      label="Months and years"
      placeholder="Select period(s)"
      [value]="calculatePeriods"
      [disabled]="isCalculating"
      (optionselected)="onCalculatePeriodChange($event)">
      @for (period of periodOptions; track period.value) {
        <mc-option [value]="period.value">{{ period.label }}</mc-option>
      }
    </mc-multi-select>
  </div>
  @if (calculateStatuses.length) {
    <ul class="calculate-status-list">
      @for (status of calculateStatuses; track status.value) {
        <li class="calculate-status-item">
          @switch (status.state) {
            @case ('success') {
              <mc-icon icon="check-circle" class="mds-success__text-color"></mc-icon>
              <span class="mds-body">Succeeded for {{ status.label }}</span>
            }
            @case ('error') {
              <mc-icon icon="exclamation-circle" class="mds-error__text-color"></mc-icon>
              <span class="mds-body">Failed for {{ status.label }}</span>
            }
            @default {
              <mc-icon icon="clock" class="mds-neutral--weak__text-color"></mc-icon>
              <span class="mds-body mds-neutral--weak__text-color">Calculating {{ status.label }}…</span>
            }
          }
        </li>
      }
    </ul>
  }
  <mc-button
    slot="primaryAction"
    appearance="primary"
    [loading]="isCalculating"
    [disabled]="calculateDisabled"
    (click)="confirmCalculate()"
    (keydown.enter)="confirmCalculate()"
    >Calculate</mc-button
  >
  <mc-button
    slot="secondaryAction"
    appearance="neutral"
    variant="outlined"
    (click)="showCalculateModal = false"
    (keydown.enter)="showCalculateModal = false"
    >Cancel</mc-button
  >
</mc-modal>

<div class="vsc-settings-page">
  <mc-tab-bar [currentindex]="activeTabIndex" (tabchange)="onTabChange($event)">
    <mc-tab slot="tab" label="Global settings" icon="globe"></mc-tab>
    <mc-tab slot="tab" label="Vendor specific settings" icon="store"></mc-tab>
  </mc-tab-bar>

  <!-- ══ Global Settings panel ══ -->
  @if (activeTabIndex === 0) {
    <div class="tab-panel">
      <p class="mds-body mds-neutral--default__on-background-color panel-intro">
        Configure system-wide KPI score binning that applies to all vendors. Consecutive score bands must be exactly 0.1 apart, so each band
        begins 0.1 from where the previous band ends.
      </p>

      <div class="binning-sections">
        @for (group of apiKpiGroups; track group.id) {
          <mc-accordion [withborder]="true">
            <mc-accordion-item [id]="'global-' + group.id" [heading]="group.label">
              <div slot="body">
                <app-vsc-kpi-binning-table
                  [kpis]="group.kpis"
                  [categoryId]="group.id"
                  [readonly]="!canManageVscSettings"
                  [hideSave]="!canManageVscSettings"
                  [showUnit]="true"
                  [toastMessage]="group.label + ' KPI score binning saved successfully.'"></app-vsc-kpi-binning-table>
              </div>
            </mc-accordion-item>
          </mc-accordion>
        }
      </div>

      <div class="calculate-actions">
        @if (canManageVscSettings) {
          <mc-button
            fit="small"
            appearance="primary"
            variant="filled"
            label="Re-calculate scores"
            (click)="openCalculateModal(true)"
            (keydown.enter)="openCalculateModal(true)"></mc-button>
        }
      </div>
    </div>
  }

  <!-- ══ Vendor Settings panel ══ -->
  @if (activeTabIndex === 1) {
    <div class="tab-panel">
      <!-- Profile cards + detail -->
      <app-vsc-settings-profiles
        (profileSelect)="onProfileSelect($event)"
        (createCopy)="onCreateCopy($event)"
        (profileDeleted)="onProfileDeleted()"></app-vsc-settings-profiles>

      @if (selectedProfileId && selectedProfile) {
        <!-- ── Profile Members (tags + vendor codes) ── -->
        <div class="section">
          <div class="section-header">
            <span class="mds-headline--small mds-info__text-color">{{
              isCreateCopyMode ? 'New copy' : (selectedProfile!.tags.slice(0, 2).join(', ') || 'Vendor settings' | titlecase)
            }}</span>
            <hr class="section-divider" />
            @if (!isCreateCopyMode) {
              @if (canManageVscSettings) {
                @if (!isCurrentProfileDefault) {
                  <mc-button
                    fit="small"
                    appearance="error"
                    icon="trash"
                    label="Delete"
                    (click)="showDeleteModal = true"
                    (keydown.enter)="showDeleteModal = true"></mc-button>
                }
                <mc-button
                  fit="small"
                  appearance="neutral"
                  variant="filled"
                  [label]="'Recalculate score for ' + calculateVendorIds.length + ' vendors'"
                  [disabled]="!calculateVendorIds.length"
                  (click)="openCalculateModal()"
                  (keydown.enter)="openCalculateModal()"></mc-button>
                <mc-button
                  fit="small"
                  appearance="secondary"
                  icon="copy"
                  label="Create Copy"
                  (click)="onCreateCopy(selectedProfile!)"
                  (keydown.enter)="onCreateCopy(selectedProfile!)"></mc-button>
              }
            }
            @if (canManageVscSettings) {
              <mc-button
                fit="small"
                appearance="primary"
                icon="floppy-disk"
                label="Save"
                [disabled]="saveDisabled"
                [loading]="isSaving"
                (click)="save()"
                (keydown.enter)="save()"></mc-button>
            }
          </div>
          <app-vsc-profile-members
            [vendorIds]="isCreateCopyMode ? [] : selectedProfile.vendorIds"
            [tags]="isCreateCopyMode ? [] : selectedProfile.tags"
            [readonly]="isProfileReadOnly"
            (vendorIdsChange)="currentVendorIds = $event"
            (tagsChange)="currentTags = $event"></app-vsc-profile-members>
        </div>

        <!-- ── KPI Weights section ── -->
        <div class="section">
          <div class="section-header">
            <span class="mds-headline-4 mds-neutral--default__on-background-color">KPI Weights</span>
          </div>
          <app-vsc-kpi-weights
            [kpiGroups]="kpiGroups"
            [kpiWeights]="kpiWeights"
            [categoryWeights]="categoryWeights"
            [readonly]="!canManageVscSettings"
            (kpiWeightsChange)="onKpiWeightsChange($event)"
            (categoryWeightsChange)="onCategoryWeightsChange($event)"></app-vsc-kpi-weights>
        </div>

        <!-- ── KPI Category Score Binning ── -->
        <div class="section">
          <div class="section-header">
            <span class="mds-headline-4 mds-neutral--default__on-background-color">KPI Category Score Binning</span>
          </div>
          <mc-accordion [withborder]="true">
            <mc-accordion-item id="vendor-category-binning" heading="Score Ranges" expanded>
              <div slot="body">
                <app-vsc-kpi-binning-table
                  [kpis]="kpiGroupsAsItems"
                  [readonly]="!canManageVscSettings"
                  [hideSave]="true"
                  [disabledIds]="disabledBinningIds"
                  [maxScore5]="maxScore5ForBinning"
                  [requireCompleteRanges]="true"
                  toastMessage="KPI category score binning saved successfully."
                  (bandsChange)="onCategoryBandsChange($event)"
                  (validityChange)="onCategoryBinningValidityChange($event)"></app-vsc-kpi-binning-table>
              </div>
            </mc-accordion-item>
          </mc-accordion>
        </div>

        <div class="save-actions">
          @if (saveDisabledReason) {
            <span class="mds-body-small save-hint">{{ saveDisabledReason }}</span>
          }
          @if (canManageVscSettings) {
            <mc-button
              fit="small"
              appearance="primary"
              icon="floppy-disk"
              label="Save"
              [disabled]="saveDisabled"
              [loading]="isSaving"
              (click)="save()"
              (keydown.enter)="save()"></mc-button>
          }
        </div>
      }
    </div>
  }
</div>
